import { CheckCircle, Loader2, XCircle } from 'lucide-react';

import { SPINNER_MOTION } from '@/components/ui/spinner';
import type {
  SeedCredentialResult,
  SubmitStageStatus,
} from '@/sections/seeds/components/modals/AddSeedByTypeModal/hooks/useAddSeedSubmit';
import { SeedTypeOption } from '@/types';
import { cn } from '@/utils/classname';

import { labelFor, SEED_TYPE_LABEL } from './submitProgressHelpers';

interface SubmitProgressStepProps {
  seedType: SeedTypeOption | null;
  seedsStatus: SubmitStageStatus;
  credentialsStatus: SubmitStageStatus;
  /** Per-seed credential outcomes (empty when credentials are skipped). */
  credentialResults?: SeedCredentialResult[];
  /**
   * Hide the two global progress rows (seeds + credentials) but keep the
   * per-seed failure list. The recorder flow folds those two rows INTO each
   * recorder table (via buildRecorderLeadingSteps), so rendering them here too
   * would duplicate them — but the failure list (which has no recorder row for
   * identities that never spawned a conversation) must still be surfaced.
   */
  hideGlobalSteps?: boolean;
}

export function SubmitProgressStep({
  seedType,
  seedsStatus,
  credentialsStatus,
  credentialResults = [],
  hideGlobalSteps = false,
}: SubmitProgressStepProps) {
  const typeLabel = seedType ? SEED_TYPE_LABEL[seedType] : 'Seed';
  const failedSeeds = credentialResults.filter(r => !r.ok).map(r => r.seed);

  // When the global rows are folded into the recorder tables and there are no
  // per-seed failures to surface, this component would render only two empty
  // wrapper <div>s. As a flex child of the parent's `gap-4` container, that
  // empty shell still reserves ~16px of dead space above the first recorder
  // table — so render nothing in that case.
  const hasFailureList =
    credentialsStatus === 'error' && failedSeeds.length > 0;
  if (hideGlobalSteps && !hasFailureList) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="w-full space-y-3">
        {!hideGlobalSteps && (
          <ProgressRow
            status={seedsStatus}
            pendingLabel={`Adding ${typeLabel}(s)...`}
            successLabel={`${typeLabel}(s) added`}
            errorLabel={`Failed to add ${typeLabel}(s)`}
          />
        )}
        {!hideGlobalSteps && credentialsStatus !== 'skipped' && (
          <ProgressRow
            status={credentialsStatus}
            pendingLabel="Adding Credentials..."
            successLabel="Credentials added"
            errorLabel="Failed to add credentials for some seeds"
          />
        )}
        {/* Per-seed credential outcomes — surface WHICH seeds failed so the
            operator knows exactly which ones are unauthenticated (P0-1(b)). Seed
            identifiers are not secrets, so rendering them is safe. */}
        {hasFailureList && (
          <ul className="border-chariot-stroke ml-8 space-y-1 border-l pl-3">
            {failedSeeds.map(seed => (
              <li
                key={seed}
                className="text-chariot-text-secondary flex items-center gap-2 text-xs"
              >
                <XCircle className="text-chariot-stroke-critical size-4 shrink-0" />
                <span className="truncate">{seed}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

interface ProgressRowProps {
  status: SubmitStageStatus;
  pendingLabel: string;
  successLabel: string;
  errorLabel: string;
}

function ProgressRow({
  status,
  pendingLabel,
  successLabel,
  errorLabel,
}: ProgressRowProps) {
  return (
    <div className="flex items-center gap-3">
      <StatusIcon status={status} />
      <span className="text-chariot-text-primary text-sm">
        {labelFor(status, pendingLabel, successLabel, errorLabel)}
      </span>
    </div>
  );
}

function StatusIcon({ status }: { status: SubmitStageStatus }) {
  switch (status) {
    case 'pending':
      return (
        <Loader2
          className={cn('text-chariot-brand-primary size-5', SPINNER_MOTION)}
        />
      );
    case 'success':
      return <CheckCircle className="text-success size-5" />;
    case 'error':
      return <XCircle className="text-chariot-stroke-critical size-5" />;
    case 'idle':
    case 'skipped':
    default:
      return (
        <div className="border-chariot-stroke size-5 rounded-full border" />
      );
  }
}
